// janus-engine/spells/spell-prep-limits.ts — Lane L7b (2026-09-27): the PURE browser twin of calculate_spellcasting_limits()
// + get_max_spell_level_castable() (includes/functions.php). The class facts come from the prepared-spell pool payload
// (ft_spell_prep_class_meta(): PlayableClass as {item_id, item_name, caster_type variable name, essence name}), so no PHP
// constant is copied here. Parity: tools/test/janus-spell-prep-limits-parity.test.mjs over every PlayableClass × levels 0–41
// × essence totals, against tools/test/fixtures/janus-spell-prep-limits.json (captured by tools/probes/spell_prep_limits_fixture.php).

export interface PrepClassMeta {
    item_id: string;
    item_name: string;
    /** CasterType variable name: 'full_caster' | 'half_caster' | 'no_bending_powers' (null = unknown id). */
    caster_type: string | null;
    /** get_essence_name(primary_essence): 'body' | 'mind' | 'soul' | null. */
    essence: string | null;
}

export interface SpellcastingLimits {
    max_class_cantrips_known: number;
    max_class_spells_known: number;
    max_spell_level_castable: number;
}

// get_max_spell_level_castable()'s map: [start, end (exclusive), max level]; the last band is open-ended (PHP_INT_MAX).
const BANDS: Record<string, ReadonlyArray<readonly [number, number, number]>> = {
    full_caster: [[1, 5, 1], [5, 9, 2], [9, 13, 3], [13, 17, 4], [17, 21, 5], [21, 25, 6], [25, 29, 7], [29, 33, 8], [33, Infinity, 9]],
    half_caster: [[1, 9, 1], [9, 17, 2], [17, 25, 3], [25, 33, 4], [33, Infinity, 5]],
};

/** get_max_spell_level_castable($caster_type, $caster_level): the band containing the level, else 0. */
export function maxSpellLevelCastable(casterType: string | null | undefined, casterLevel: number): number {
    const bands = casterType ? BANDS[casterType] : undefined;
    if (!bands) return 0;
    for (const [start, end, max] of bands) if (casterLevel >= start && casterLevel < end) return max;
    return 0;
}

/** PHP (int) cast of a scalar: numeric strings / floats truncate toward zero, anything else is 0. */
export function phpInt(v: unknown): number {
    if (typeof v === 'boolean') return v ? 1 : 0;
    const n = typeof v === 'number' ? v : typeof v === 'string' ? parseFloat(v) : NaN;
    return Number.isFinite(n) ? Math.trunc(n) : 0;
}

type AnyRec = Record<string, unknown>;
const rec = (v: unknown): AnyRec | null => (v !== null && typeof v === 'object' && !Array.isArray(v) ? (v as AnyRec) : null);

/** compute_essence($caster, $name): computed.creature.essences.<name>.total, else the pre-move computed.essences, else 0. */
export function essenceTotal(row: unknown, essenceName: string): number {
    const computed = rec(rec(row)?.computed);
    const fresh = rec(rec(rec(computed?.creature)?.essences)?.[essenceName]);
    if (fresh && fresh.total !== undefined && fresh.total !== null) return phpInt(fresh.total);
    const legacy = rec(rec(computed?.essences)?.[essenceName]);
    if (legacy && legacy.total !== undefined && legacy.total !== null) return phpInt(legacy.total);
    return 0;
}

const ZERO: SpellcastingLimits = { max_class_cantrips_known: 0, max_class_spells_known: 0, max_spell_level_castable: 0 };

/** calculate_spellcasting_limits($class_id, $class_level, $caster): full casters 2+E cantrips / ceil(L/2)+E spells, half casters
 * E cantrips / ceil(L/4)+E spells (each at least 1); anything else — an unknown class included — is all zeros. */
export function spellcastingLimits(meta: PrepClassMeta | null | undefined, classLevel: number, row: unknown): SpellcastingLimits {
    if (!meta) return { ...ZERO };
    const essence = meta.essence !== null && meta.essence !== undefined ? essenceTotal(row, meta.essence) : 0;
    let cantrips: number, spells: number;
    if (meta.caster_type === 'full_caster') {
        cantrips = Math.max(1, 2 + essence);
        spells = Math.max(1, Math.ceil(classLevel / 2) + essence);
    } else if (meta.caster_type === 'half_caster') {
        cantrips = Math.max(1, 0 + essence);
        spells = Math.max(1, Math.ceil(classLevel / 4) + essence);
    } else {
        return { ...ZERO };
    }
    return { max_class_cantrips_known: cantrips, max_class_spells_known: spells, max_spell_level_castable: maxSpellLevelCastable(meta.caster_type, classLevel) };
}
